/**
 * The emoji Stitch offers for reactions and statuses: a short, work-friendly set, each with the
 * words it's found by. (Any single emoji typed elsewhere works too; the server checks it's one.)
 */

export interface EmojiGroup {
  name: string;
  /** [emoji, its name, more words it's found by] */
  list: [string, string, string][];
}

export const EMOJI_GROUPS: EmojiGroup[] = [
  {
    name: 'Smileys',
    list: [
      ['😀', 'grinning face', 'grinning smile happy'],
      ['😃', 'smiley', 'smiley happy'],
      ['😄', 'smile', 'smile happy'],
      ['😁', 'grin', 'grin'],
      ['😆', 'laughing', 'laughing'],
      ['😅', 'sweat smile', 'sweat smile relief'],
      ['😂', 'tears of joy', 'joy tears laughing'],
      ['🤣', 'rolling on the floor laughing', 'rolling on the floor laughing'],
      ['🙂', 'slightly smiling', 'slightly smiling'],
      ['🙃', 'upside down', 'upside down'],
      ['😉', 'wink', 'wink'],
      ['😊', 'blush', 'blush smiling'],
      ['😇', 'halo', 'innocent halo'],
      ['🥰', 'smiling with hearts', 'smiling hearts love'],
      ['😍', 'heart eyes', 'heart eyes love'],
      ['🤩', 'star struck', 'star struck'],
      ['😘', 'kiss', 'kiss'],
      ['😋', 'yum', 'yum delicious'],
      ['😛', 'tongue out', 'tongue'],
      ['😜', 'winking with tongue', 'winking tongue'],
      ['🤪', 'zany', 'zany crazy'],
      ['🤗', 'hug', 'hug hugging'],
      ['🤭', 'oops', 'oops hand over mouth'],
      ['🤫', 'shush', 'shush quiet'],
      ['🤔', 'thinking', 'thinking hmm'],
      ['🤐', 'zipper mouth', 'zipper mouth secret'],
      ['😐', 'neutral', 'neutral'],
      ['😑', 'expressionless', 'expressionless'],
      ['😶', 'speechless', 'no mouth speechless'],
      ['😏', 'smirk', 'smirk'],
      ['😒', 'unamused', 'unamused'],
      ['🙄', 'eye roll', 'eye roll'],
      ['😬', 'grimace', 'grimace awkward'],
      ['😌', 'relieved', 'relieved'],
      ['😔', 'pensive', 'pensive'],
      ['😴', 'sleeping', 'sleeping tired'],
      ['😷', 'mask', 'mask sick'],
      ['🤒', 'sick', 'thermometer sick ill'],
      ['🤧', 'sneezing', 'sneeze cold'],
      ['🥵', 'hot', 'hot'],
      ['🥶', 'cold', 'cold freezing'],
      ['🥴', 'woozy', 'woozy'],
      ['🤯', 'mind blown', 'mind blown'],
      ['🤠', 'cowboy', 'cowboy'],
      ['🥳', 'party face', 'party celebrate'],
      ['😎', 'sunglasses', 'cool sunglasses'],
      ['🤓', 'nerd', 'nerd'],
      ['🧐', 'monocle', 'monocle'],
      ['😕', 'confused', 'confused'],
      ['😟', 'worried', 'worried'],
      ['🙁', 'frown', 'frown sad'],
      ['😮', 'wow', 'wow open mouth surprised'],
      ['😲', 'astonished', 'astonished shocked'],
      ['😳', 'flushed', 'flushed embarrassed'],
      ['🥺', 'pleading', 'pleading please'],
      ['😢', 'crying', 'cry sad'],
      ['😭', 'sobbing', 'sob crying'],
      ['😱', 'scream', 'scream'],
      ['😩', 'weary', 'weary'],
      ['😫', 'tired', 'tired'],
      ['🥱', 'yawn', 'yawn'],
      ['😤', 'huff', 'huff triumph'],
      ['😠', 'angry', 'angry'],
      ['😡', 'furious', 'pout rage'],
      ['😈', 'devil', 'devil imp'],
      ['💀', 'skull', 'skull dead'],
      ['💩', 'poop', 'poop'],
      ['🤡', 'clown', 'clown'],
      ['👻', 'ghost', 'ghost'],
      ['🤖', 'robot', 'robot']
    ]
  },
  {
    name: 'Hands and people',
    list: [
      ['👍', 'thumbs up', 'thumbs up yes like +1'],
      ['👎', 'thumbs down', 'thumbs down no -1'],
      ['👌', 'OK hand', 'ok okay'],
      ['✌️', 'victory', 'victory peace'],
      ['🤞', 'fingers crossed', 'fingers crossed luck'],
      ['🤟', 'love you', 'love you'],
      ['🤘', 'rock on', 'rock on'],
      ['🤙', 'call me', 'call me'],
      ['👈', 'pointing left', 'point left'],
      ['👉', 'pointing right', 'point right'],
      ['👆', 'pointing up', 'point up'],
      ['👇', 'pointing down', 'point down'],
      ['☝️', 'index finger up', 'index up'],
      ['👋', 'wave', 'wave hello bye'],
      ['✋', 'raised hand', 'raised hand stop high five'],
      ['🖐️', 'hand', 'hand'],
      ['🖖', 'vulcan salute', 'vulcan'],
      ['👏', 'clapping', 'clap applause'],
      ['🙌', 'raised hands', 'raised hands celebrate'],
      ['👐', 'open hands', 'open hands'],
      ['🤲', 'palms up', 'palms up'],
      ['🤝', 'handshake', 'handshake deal'],
      ['🙏', 'thank you', 'pray please thanks'],
      ['✍️', 'writing', 'writing'],
      ['💪', 'muscle', 'muscle strong'],
      ['👀', 'eyes', 'eyes looking'],
      ['🧠', 'brain', 'brain smart'],
      ['🤷', 'shrug', 'shrug'],
      ['🙋', 'raising hand', 'raising hand me'],
      ['🙅', 'no', 'no gesture'],
      ['🙆', 'OK', 'ok gesture'],
      ['💁', 'tipping hand', 'tipping hand'],
      ['🤦', 'facepalm', 'facepalm'],
      ['🏃', 'running', 'running'],
      ['🚶', 'walking', 'walking'],
      ['👶', 'baby', 'baby'],
      ['🧑‍💻', 'at the computer', 'technologist computer'],
      ['🧑‍⚕️', 'health worker', 'health worker doctor nurse'],
      ['🦷', 'tooth', 'tooth dental']
    ]
  },
  {
    name: 'Hearts and symbols',
    list: [
      ['❤️', 'red heart', 'red heart love'],
      ['🧡', 'orange heart', 'orange heart'],
      ['💛', 'yellow heart', 'yellow heart'],
      ['💚', 'green heart', 'green heart'],
      ['💙', 'blue heart', 'blue heart'],
      ['💜', 'purple heart', 'purple heart'],
      ['🖤', 'black heart', 'black heart'],
      ['🤍', 'white heart', 'white heart'],
      ['💔', 'broken heart', 'broken heart'],
      ['💯', 'hundred', 'hundred 100 perfect'],
      ['✅', 'done', 'check done yes'],
      ['☑️', 'checked box', 'ballot check'],
      ['✔️', 'check mark', 'check mark'],
      ['❌', 'cross', 'cross no wrong'],
      ['⭕', 'circle', 'circle'],
      ['❗', 'exclamation', 'exclamation important'],
      ['❓', 'question', 'question'],
      ['⚠️', 'warning', 'warning caution'],
      ['🚫', 'prohibited', 'prohibited no'],
      ['⛔', 'no entry', 'no entry'],
      ['🔥', 'fire', 'fire hot lit'],
      ['⭐', 'star', 'star'],
      ['🌟', 'glowing star', 'glowing star'],
      ['✨', 'sparkles', 'sparkles'],
      ['⚡', 'lightning', 'lightning zap'],
      ['💡', 'idea', 'idea bulb'],
      ['💬', 'speech bubble', 'speech bubble'],
      ['🔔', 'bell', 'bell'],
      ['🔕', 'bell off', 'bell off mute'],
      ['➕', 'plus', 'plus add'],
      ['➖', 'minus', 'minus'],
      ['🆗', 'OK button', 'ok button'],
      ['🆕', 'new', 'new'],
      ['🆘', 'SOS', 'sos help'],
      ['🔴', 'red circle', 'red circle'],
      ['🟠', 'orange circle', 'orange circle'],
      ['🟡', 'yellow circle', 'yellow circle'],
      ['🟢', 'green circle', 'green circle'],
      ['🔵', 'blue circle', 'blue circle'],
      ['🟣', 'purple circle', 'purple circle']
    ]
  },
  {
    name: 'Work',
    list: [
      ['🎉', 'party popper', 'party popper tada celebrate'],
      ['🎊', 'confetti', 'confetti'],
      ['🎈', 'balloon', 'balloon'],
      ['🎂', 'birthday cake', 'birthday cake'],
      ['🎁', 'gift', 'gift present'],
      ['🏆', 'trophy', 'trophy win'],
      ['🥇', 'gold medal', 'gold medal first'],
      ['🚀', 'rocket', 'rocket launch ship'],
      ['📈', 'chart going up', 'chart up growth'],
      ['📉', 'chart going down', 'chart down'],
      ['⏰', 'alarm clock', 'alarm clock'],
      ['⏳', 'hourglass', 'hourglass waiting'],
      ['🕒', 'clock', 'clock time'],
      ['📅', 'calendar', 'calendar date'],
      ['🗓️', 'meeting', 'calendar meeting'],
      ['📌', 'pin', 'pin'],
      ['📎', 'paperclip', 'paperclip attachment'],
      ['📞', 'phone receiver', 'telephone receiver call'],
      ['☎️', 'telephone', 'telephone phone'],
      ['📱', 'mobile phone', 'mobile phone'],
      ['📠', 'fax', 'fax'],
      ['💻', 'laptop', 'laptop computer'],
      ['🖥️', 'desktop computer', 'desktop computer'],
      ['🖨️', 'printer', 'printer'],
      ['🎧', 'headset', 'headphones headset'],
      ['📧', 'email', 'email'],
      ['📨', 'incoming mail', 'incoming envelope'],
      ['📝', 'memo', 'memo note'],
      ['📋', 'clipboard', 'clipboard'],
      ['📁', 'folder', 'folder'],
      ['📦', 'package', 'package box delivery'],
      ['🔑', 'key', 'key'],
      ['🔒', 'lock', 'lock locked'],
      ['💰', 'money bag', 'money bag'],
      ['💵', 'dollars', 'dollar money'],
      ['💳', 'credit card', 'credit card'],
      ['🧾', 'receipt', 'receipt'],
      ['🏥', 'hospital', 'hospital'],
      ['🩺', 'stethoscope', 'stethoscope doctor'],
      ['💊', 'pill', 'pill medicine'],
      ['🏢', 'office', 'office building'],
      ['🏠', 'home', 'house home'],
      ['🚗', 'car', 'car commute'],
      ['🚌', 'bus', 'bus'],
      ['✈️', 'airplane', 'airplane travel'],
      ['🌴', 'palm tree', 'palm tree vacation'],
      ['🏖️', 'beach', 'beach vacation'],
      ['🛠️', 'tools', 'tools fix'],
      ['🧹', 'broom', 'broom clean'],
      ['🤒', 'sick', 'sick ill']
    ]
  },
  {
    name: 'Food and nature',
    list: [
      ['☕', 'coffee', 'coffee'],
      ['🍵', 'tea', 'tea'],
      ['🥤', 'drink', 'drink'],
      ['🍕', 'pizza', 'pizza'],
      ['🍔', 'burger', 'burger'],
      ['🌮', 'taco', 'taco'],
      ['🥗', 'salad', 'salad'],
      ['🍜', 'noodles', 'noodles ramen'],
      ['🍣', 'sushi', 'sushi'],
      ['🥪', 'sandwich', 'sandwich lunch'],
      ['🍽️', 'lunch', 'plate lunch dinner'],
      ['🍩', 'doughnut', 'doughnut donut'],
      ['🍪', 'cookie', 'cookie'],
      ['🍰', 'cake', 'cake'],
      ['🍫', 'chocolate', 'chocolate'],
      ['🍎', 'apple', 'apple'],
      ['🍌', 'banana', 'banana'],
      ['🍻', 'cheers', 'beers cheers'],
      ['🥂', 'toast', 'champagne cheers'],
      ['☀️', 'sun', 'sun sunny'],
      ['🌤️', 'sun and clouds', 'sun cloud'],
      ['🌧️', 'rain', 'rain'],
      ['⛈️', 'storm', 'storm'],
      ['❄️', 'snowflake', 'snow cold'],
      ['🌈', 'rainbow', 'rainbow'],
      ['🌊', 'wave of water', 'wave ocean'],
      ['🌸', 'blossom', 'blossom flower'],
      ['🌻', 'sunflower', 'sunflower'],
      ['🍀', 'four-leaf clover', 'clover luck'],
      ['🌲', 'tree', 'tree'],
      ['🐶', 'dog', 'dog'],
      ['🐱', 'cat', 'cat'],
      ['🐻', 'bear', 'bear'],
      ['🦄', 'unicorn', 'unicorn'],
      ['🐢', 'turtle', 'turtle slow'],
      ['🦋', 'butterfly', 'butterfly']
    ]
  }
];

const ALL = EMOJI_GROUPS.flatMap((g) => g.list);
const NAMES = new Map(ALL.map(([e, name]) => [e, name]));

/** A name for an emoji, for people using a screen reader ("thumbs up"). */
export function emojiName(emoji: string): string {
  return NAMES.get(emoji) ?? NAMES.get(`${emoji}️`) ?? NAMES.get(emoji.replace(/️/g, '')) ?? 'emoji';
}

/** Emoji whose words start with what's typed (every word typed). */
export function searchEmoji(query: string): string[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const [e, name, more] of ALL) {
    const mine = `${name} ${more}`.toLowerCase().split(/[\s-]+/);
    if (!seen.has(e) && words.every((q) => mine.some((x) => x.startsWith(q)))) {
      seen.add(e);
      out.push(e);
    }
  }
  return out;
}

const RECENT_KEY = 'stitch.emoji.recent';

/** The emoji this person used last on this device (newest first). */
export function recentEmoji(): string[] {
  try {
    const v = JSON.parse(window.localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length <= 32).slice(0, 16) : [];
  } catch {
    return [];
  }
}

export function rememberEmoji(emoji: string): void {
  quick = null;
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify([emoji, ...recentEmoji().filter((x) => x !== emoji)].slice(0, 16)));
  } catch {
    // Not kept: nothing else depends on it.
  }
}

let quick: string[] | null = null;
/** Three emoji one click away on a message: the ones used last, then 👍 ✅ 👀. */
export function quickEmoji(): string[] {
  quick ??= [...new Set([...recentEmoji(), '👍', '✅', '👀'])].slice(0, 3);
  return quick;
}
